import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getSignedUrlCookieKey } from '@/utils/signedUrlCache';

interface CachedSignedUrl {
  url: string;
  timestamp: number;
}

export const useSignedUrl = (fileKey: string | null, bucket: 'gym-logos' | 'gym-documents') => {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getCookieKey = (fileKey: string, bucket: string) => {
    return getSignedUrlCookieKey(fileKey, bucket);
  };

  const getCachedUrl = (cookieKey: string): string | null => {
    try {
      const cookieValue = document.cookie
        .split('; ')
        .find(row => row.startsWith(`${cookieKey}=`))
        ?.split('=')[1];

      if (!cookieValue) return null;

      const decoded = decodeURIComponent(cookieValue);
      const cached: CachedSignedUrl = JSON.parse(decoded);
      
      // Check if URL is still valid (24 hours = 86400000 ms)
      const now = Date.now();
      if (now - cached.timestamp > 86400000) {
        // URL expired, remove cookie
        document.cookie = `${cookieKey}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        return null;
      }

      return cached.url;
    } catch (err) {
      console.warn('Error reading cached signed URL:', err);
      return null;
    }
  };

  const setCachedUrl = (cookieKey: string, url: string) => {
    try {
      const cached: CachedSignedUrl = {
        url,
        timestamp: Date.now()
      };
      
      const encoded = encodeURIComponent(JSON.stringify(cached));
      // Set cookie to expire in 24 hours
      const expiryDate = new Date(Date.now() + 86400000); // 24 hours
      document.cookie = `${cookieKey}=${encoded}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
    } catch (err) {
      console.warn('Error caching signed URL:', err);
    }
  };

  useEffect(() => {
    const getSignedUrl = async () => {
      if (!fileKey) {
        setSignedUrl(null);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const cookieKey = getCookieKey(fileKey, bucket);
        
        // Check if we have a cached URL
        const cachedUrl = getCachedUrl(cookieKey);
        if (cachedUrl) {
          console.log('🍪 Using cached signed URL for:', { fileKey, bucket });
          setSignedUrl(cachedUrl);
          setIsLoading(false);
          return;
        }

        console.log('🔗 Generating new signed URL for:', { fileKey, bucket });
        
        const { data, error } = await supabase.storage
          .from(bucket)
          .createSignedUrl(fileKey, 86400); // 24 hour expiry

        if (error) throw error;

        const newSignedUrl = data?.signedUrl || null;
        setSignedUrl(newSignedUrl);
        
        // Cache the new URL
        if (newSignedUrl) {
          setCachedUrl(cookieKey, newSignedUrl);
        }
        
        console.log('✅ Signed URL generated and cached successfully');
      } catch (err) {
        console.error('❌ Error generating signed URL:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        setSignedUrl(null);
      } finally {
        setIsLoading(false);
      }
    };

    getSignedUrl();
  }, [fileKey, bucket]);

  return { signedUrl, isLoading, error };
};